import TactonConfigButton from "@husky-hrc/Components/Tacton/TactonConfigButton";
import { huskyThemedButtonProps } from "@husky-hrc/Utilities/Mixins/buttons";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation, removeAbsoluteUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    labelIfLoggedOut = "labelIfLoggedOut",
    labelIfLoggedIn = "labelIfLoggedIn",
    labelIfAccountBlocked = "labelIfAccountBlocked",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.labelIfLoggedOut]: string;
        [fields.labelIfLoggedIn]: string;
        [fields.labelIfAccountBlocked]: string;
    };
}

type Props = OwnProps & WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    returnUrl: removeAbsoluteUrl(getLocation(state).pathname),
});

export interface TactonConfigButtonStyles {
    containerStyles?: InjectableCss;
    button?: ButtonPresentationProps;
}

export const TactonConfigButtonStyles: TactonConfigButtonStyles = {
    containerStyles: {
        css: css`
            position: relative;
            display: inline-block;
            padding-right: 2rem;

            :focus-within {
                button::after {
                    display: none;
                }
            }

            button:hover {
                transition: none;
                background-color: ${getColor("primary.contrast")};
                background: ${getColor("primary.contrast")};
            }

            button:hover::after {
                background-color: transparent;
                background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='832' height='46' viewBox='0 0 832 46'><path fill='hsl(44.10000000000002,100%,41%)' d='M3460,3261h416l-7,46H3460v-46Zm0,46H3044l7-46h409' transform='translate(-3044 -3261)'/></svg>%0A");
                transition: none;
            }
        `,
    },
    button: huskyThemedButtonProps(
        "primary",
        "slanted",
        css`
            button {
                all: unset;
                display: inline-block;
                vertical-align: middle;
                margin: 0 0 1rem 0;
                padding: 1rem;
                border: 0;
                border-radius: 0;
                font-family: inherit;
                font-size: 1rem;
                -webkit-appearance: none;
                line-height: 1;
                text-align: center;
                cursor: pointer;
                font-weight: 900;
                text-decoration: none;
                margin-bottom: 0;
                transition: none;
            }

            &:after {
                all: unset;
                content: "";
                display: block;
                pointer-events: none;
                background-position: top right;
                position: absolute;
                top: 0;
                bottom: 0;
                right: -2rem;
                width: 2rem;
                height: 100%;
                background-color: transparent;
                background-size: cover;
                background-repeat: no-repeat;
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='832' height='46' viewBox='0 0 832 46'%3E%3Cpath fill='%23ffc72c' d='M3460,3261h416l-7,46H3460v-46Zm0,46H3044l7-46h409' transform='translate(-3044 -3261)'/%3E%3C/svg%3E%0A");
                transition: none;
            }
        `,
    ),
};

const styles = TactonConfigButtonStyles;

const ProductDetailsTactonConfigButton = ({ fields, returnUrl }: Props) => {
    return (
        <StyledWrapper {...styles.containerStyles}>
            <TactonConfigButton
                extendedStyles={styles.button}
                data-test-selector="addProductToCart"
                labelIfLoggedOut={fields.labelIfLoggedOut}
                labelIfLoggedIn={fields.labelIfLoggedIn}
                labelIfAccountBlocked={fields.labelIfAccountBlocked}
                returnUrl={returnUrl}
            />
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    displayName: "Tacton Configuration Button",
    group: "Product Details",
    allowedContexts: ["ProductDetailsPage"],
    fieldDefinitions: [
        {
            name: fields.labelIfLoggedOut,
            displayName: "Label (Logged Out)",
            fieldType: "Translatable",
            editorTemplate: "TextField",
            defaultValue: "Register For An Account To Configure This Product",
        },
        {
            name: fields.labelIfLoggedIn,
            displayName: "Label (Logged In)",
            fieldType: "Translatable",
            editorTemplate: "TextField",
            defaultValue: "Configure This Product",
        },
        {
            name: fields.labelIfAccountBlocked,
            displayName: "Label (Account Blocked)",
            fieldType: "Translatable",
            editorTemplate: "TextField",
            defaultValue: "Please Contact Customer Service",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsTactonConfigButton)),
    definition,
};

export default widgetModule;
